/**
 * Personal Mode Limits for Clone Table
 *
 * Defines the row count limits for Personal mode when cloning with data.
 * Schema-only clones are not restricted.
 */

/**
 * Maximum row count for clone with data in Personal mode (100,000 rows).
 * Tables larger than this require Pro mode for data cloning.
 */
export const PERSONAL_MODE_MAX_ROWS = 100_000;

/**
 * Check result for Personal mode clone table limits
 */
export type PersonalModeLimitCheck = {
  /** Whether the table exceeds Personal mode limits */
  exceedsLimit: boolean;
  /** Row count (null if unknown) */
  rowCount: number | null;
  /** Human-readable description of the limit status */
  description: string;
  /** Formatted row count string for display */
  formattedRowCount: string | null;
};

/**
 * Checks if a table exceeds Personal mode row limits for cloning with data.
 *
 * In Personal mode, cloning with data is only allowed for tables
 * <= 100,000 rows. Larger tables require Pro mode for data cloning.
 * Schema-only clones are always allowed.
 *
 * @param rowCount - Table row count (null if unknown)
 * @returns Limit check result
 */
export function checkPersonalModeLimits(rowCount: number | null): PersonalModeLimitCheck {
  // If row count is null/unknown, assume it's within limits
  // (we can't block without knowing the count)
  const exceedsLimit = rowCount !== null && rowCount > PERSONAL_MODE_MAX_ROWS;
  const formattedRowCount = rowCount !== null ? formatRowCountNumber(rowCount) : null;

  const description = getPersonalModeLimitDescription(rowCount, exceedsLimit);

  return {
    exceedsLimit,
    rowCount,
    description,
    formattedRowCount,
  };
}

/**
 * Generates a human-readable description of the limit status.
 */
function getPersonalModeLimitDescription(rowCount: number | null, exceedsLimit: boolean): string {
  if (rowCount === null) {
    return 'Unable to determine table row count.';
  }

  const formattedCount = formatRowCountNumber(rowCount);
  const limitString = formatRowCountNumber(PERSONAL_MODE_MAX_ROWS);

  if (exceedsLimit) {
    return `Table has ${formattedCount} rows, which exceeds the ${limitString} row limit for cloning with data in Personal mode.`;
  }

  return `Table row count (${formattedCount}) is within Personal mode limits.`;
}

/**
 * Formats the row limit for display in the UI.
 *
 * @returns Human-readable string describing the Personal mode limit
 */
export function getPersonalModeLimitString(): string {
  return formatRowCountNumber(PERSONAL_MODE_MAX_ROWS);
}

/**
 * Formats a row count as a bare grouped NUMBER — thousands separators only,
 * no unit noun. `formatRowCountNumber(1)` → `"1"`, `(100_000)` → `"100,000"`.
 *
 * NOT the same thing as `workspace/common/utils/formatRowCount`, which is the
 * app's row-count WORDING helper and returns `"1 row"` / `"100,000 rows"`.
 * This one was ALSO called `formatRowCount`, so a developer importing "the"
 * `formatRowCount` got either behaviour depending on the import path — the
 * trap that made Wave 4 consolidate two other copies in the first place.
 *
 * Renamed rather than folded into the shared helper because the behaviours
 * genuinely cannot be reconciled here: every caller below supplies the noun
 * itself ("Table has ${…} rows", "${…} rows or fewer"), so the shared helper
 * would render "100,000 rows rows".
 */
export function formatRowCountNumber(count: number): string {
  return count.toLocaleString();
}

/**
 * Fetches the table row count from the backend.
 * Reuses the data-compare row count estimation API.
 *
 * @param connectionId - Connection ID
 * @param databaseName - Database name
 * @param schema - Schema name
 * @param tableName - Table name
 * @returns Row count or null if unable to fetch
 */
export async function fetchTableRowCount(
  connectionId: string,
  databaseName: string,
  schema: string,
  tableName: string
): Promise<{ success: boolean; rowCount?: number | null; error?: string }> {
  try {
    if (!window.electronAPI) {
      return { success: false, error: 'Electron API not available' };
    }

    // Reuse the data-compare estimateRowCount API.
    // `kind: 'connection'` is required — DataCompareEndpoint is a discriminated
    // union and assertConnectionEndpoint in chunkIterator.ts throws for any
    // endpoint that does not carry the 'connection' discriminator.
    const result = await window.electronAPI.dataCompare.estimateRowCount({
      endpoint: {
        kind: 'connection',
        connectionId,
        database: databaseName,
        schema,
        table: tableName,
      },
    });

    return {
      success: result.success,
      rowCount: result.rowCount,
      error: result.error,
    };
  } catch (error) {
    console.error('[clone-table/personalModeLimits] Error fetching row count:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error fetching row count',
    };
  }
}
